/**
 * HTTP Client utility with dynamic backend URL resolution and authentication
 * Handles different environments (local dev, production) automatically
 */

/**
 * Get the appropriate backend URL for the current environment
 * - Development: Uses http://localhost:8001
 * - Production: Uses current host with /api prefix (nginx proxy)
 */
export const getBackendUrl = (): string => {
  // Check if we have an explicit environment variable set
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }

  // Determine environment based on hostname
  const hostname = window.location.hostname;
  
  // Development environment (localhost, 127.0.0.1, or local IP)
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname.startsWith('10.')) {
    return 'http://localhost:8001';
  }
  
  // Production environment - use current origin
  // This works because nginx proxies /api/* to backend:8001
  return window.location.origin;
};

/**
 * Create a complete API URL for the given endpoint
 * @param endpoint - API endpoint (e.g., '/auth/login', '/health')
 * @returns Complete URL for the API endpoint
 */
export const getApiUrl = (endpoint: string): string => {
  const backendUrl = getBackendUrl();
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  // For local development, use direct backend URL
  if (backendUrl.includes('localhost:8001')) {
    return `${backendUrl}/api${cleanEndpoint}`;
  }
  
  // For production, nginx handles the /api proxy
  return `${backendUrl}/api${cleanEndpoint}`;
};

// HTTP client utility with automatic authentication
export class AuthenticatedHttpClient {
  private getAuthHeader(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  async fetch(url: string, options: RequestInit = {}): Promise<Response> {
    const authHeaders = this.getAuthHeader();
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        ...authHeaders,
      },
    });

    // If we get a 401, redirect to login
    if (response.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.reload();
    }

    return response;
  }

  async post(url: string, data?: any, options: RequestInit = {}): Promise<Response> {
    return this.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async postFormData(url: string, formData: FormData, options: RequestInit = {}): Promise<Response> {
    // Don't set Content-Type for FormData, let the browser set it with boundary
    const { headers, ...restOptions } = options;
    return this.fetch(url, {
      method: 'POST',
      body: formData,
      ...restOptions,
      headers: {
        ...headers,
        // Explicitly don't set Content-Type for FormData
      },
    });
  }

  async get(url: string, options: RequestInit = {}): Promise<Response> {
    return this.fetch(url, {
      method: 'GET',
      ...options,
    });
  }
}

export const httpClient = new AuthenticatedHttpClient();

/**
 * Debug utility to check backend URL configuration
 */
export const debugBackendUrl = (): void => {
  // Debug information available in browser dev tools only
  if (process.env.NODE_ENV === 'development') {
    console.debug('Backend URL Debug Info:', {
      backendUrl: getBackendUrl(),
      healthCheckUrl: getApiUrl('/health'),
      loginUrl: getApiUrl('/auth/login'),
      hostname: window.location.hostname,
      origin: window.location.origin,
      envVar: process.env.REACT_APP_BACKEND_URL || 'not set'
    });
  }
};